import { TableGridBaseColDef, TableGridRenderCellParams } from '@components/table/types/TableColumnDef.type';
import { SubjectivityStatus } from '@lio/rule-engine';
import { Grid, IconButton, Typography } from '@mui/material';
import { OverflowTooltip } from '@components/tooltip';
import { Box } from '@mui/material';
import { Button } from '@components/button';
import { ExclamationTriangleColored, CheckCircleSolid, FileUpload, Times } from '@assets/svgs';
import { SubjectivityItem } from '@services/subjectivity';
import { SubjectivityStyle } from '@lio/rule-engine';

const SubjectivitiesFieldNames = {
  Status: 'status',
  Subjectivity: 'label',
  DueDays: 'dueDaysAfterPolicyEffective',
  Data: 'file',
  File: 'file',
  Actions: 'actions',
};

export const getSubjectivitiesColumns = (
  canUploadToSubjectivity: (subjectivity: SubjectivityItem) => boolean,
  isUnderwriter: boolean,
  isLIOContractor: boolean,
  isBroker: boolean,
  isInternalUser: boolean,
  isQuoteTerminal: boolean,
  handleUploadClick: (subjectivity: SubjectivityItem, event: React.MouseEvent) => void,
  handleDeleteClick: (subjectivity: SubjectivityItem, event: React.MouseEvent) => void,
) => {
  const canDeleteSubjectivity = (subjectivity: SubjectivityItem) => {
    if (isQuoteTerminal || subjectivity.deletedAt) return false;
    // Only allow deletion of Active subjectivities
    if (subjectivity.status !== SubjectivityStatus.Active) return false;
    // Library subjectivities cannot be deleted
    if (subjectivity.offeringSubjectivityId) return false;
    if (isInternalUser || isUnderwriter) return true;
    if (isBroker && subjectivity.isBrokerSubjectivity) return true;
    return false;
  };
  const columns: TableGridBaseColDef<SubjectivityItem>[] = [
    {
      field: SubjectivitiesFieldNames.Status,
      headerName: 'Status',
      sortable: false,
      renderCell: ({ row }) => {
        const isActive = row.status === SubjectivityStatus.Active;
        const Icon = isActive ? ExclamationTriangleColored : CheckCircleSolid;
        const color = isActive ? 'warning.main' : 'success.main';

        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              height: '100%',
              svg: {
                color: (theme) => (isActive ? theme.palette.warning.main : `${theme.palette.success.dark} !important`),
              },
            }}
          >
            <Icon style={{ height: '16px', width: '16px', color: color }} />

            <Typography variant='bodySmall' color='text.primary'>
              {isActive ? 'Open' : 'Closed'}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: SubjectivitiesFieldNames.Subjectivity,
      headerName: 'Subjectivity',
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
          {row.label && (
            <OverflowTooltip tooltipText={row.label}>
              <Typography variant='body2' fontWeight={500}>
                {row.label}
              </Typography>
            </OverflowTooltip>
          )}
          {row.description && (
            <OverflowTooltip tooltipText={row.description}>
              <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 0.5 }}>
                {row.description}
              </Typography>
            </OverflowTooltip>
          )}
        </Box>
      ),
    },
    {
      field: SubjectivitiesFieldNames.DueDays,
      headerName: 'Due Days',
      sortable: false,
      renderCell: ({ row }) => (
        <Typography variant='bodySmall'>
          {row.dueDaysAfterPolicyEffective ? `${row.dueDaysAfterPolicyEffective} days` : '-'}
        </Typography>
      ),
    },
    {
      field: SubjectivitiesFieldNames.File,
      headerName: 'Data',
      flex: 1,
      sortable: false,
      renderCell: (params: TableGridRenderCellParams) => {
        const { documents, subjectivityType, status, handleDelete, handleOpenFormDialog, handleOpenViewFilesModal } =
          params.row;

        if (subjectivityType?.style === SubjectivityStyle.Form) {
          if (status === SubjectivityStatus.Closed) {
            return (
              <Button variant='text' onClick={handleOpenFormDialog} sx={{ padding: 0 }}>
                View Details
              </Button>
            );
          }

          return '-';
        }

        if (documents.length === 1) {
          const document = documents[0];
          return (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Button
                variant='text'
                component='a'
                target='_blank'
                onClick={(e) => e.stopPropagation()}
                href={document?.file.downloadUrl ?? ''}
                disabled={document?.file.downloadUrl === undefined}
                sx={{
                  padding: '0',
                  justifyContent: 'flex-start',
                  overflow: 'hidden',
                  '&.Mui-disabled': {
                    fontWeight: '400',
                    cursor: 'default',
                    color: 'text.subdued',
                  },
                }}
              >
                <OverflowTooltip>{document.file.fileName}</OverflowTooltip>
              </Button>
              {handleDelete && (
                <IconButton
                  onClick={handleDelete}
                  sx={(theme) => ({
                    ...theme.icons.sm,
                    padding: '5px',
                    marginLeft: '4px',
                    color: theme.palette.text.subdued,
                  })}
                >
                  <Times />
                </IconButton>
              )}
            </Box>
          );
        } else if (documents.length > 1) {
          return (
            <Button variant='text' onClick={handleOpenViewFilesModal} sx={{ padding: 0 }}>
              View Files
            </Button>
          );
        }
        return '-';
      },
    },
    {
      // TODO this should be up to 2 icons
      field: SubjectivitiesFieldNames.Actions,
      headerName: '',
      sortable: false,
      renderCell: ({ row }) => {
        const canUpload = canUploadToSubjectivity(row);
        const canDelete = canDeleteSubjectivity(row);
        const canEdit = isUnderwriter || isLIOContractor || (isBroker && row.isBrokerSubjectivity);
        if (!canUpload && !canDelete && !canEdit) return null;

        return (
          <Grid
            container
            spacing={1}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end', height: '100%' }}
          >
            {canUpload && (
              <IconButton onClick={(e) => handleUploadClick(row, e)}>
                <FileUpload />
              </IconButton>
            )}
            {canDelete && (
              <IconButton onClick={(e) => handleDeleteClick(row, e)}>
                <Times />
              </IconButton>
            )}
          </Grid>
        );
      },
    },
  ];
  return columns;
};
